<script setup>
import { ref, computed, onMounted } from "vue";
import ModalManager from "@/components/modal/ModalManager";
import CpNzContractEditor from "@/components/editors/CpNzContractEditor.vue";

/* ============================================================
   State
============================================================ */
const shops = ref([]);
const currentShop = ref(null);
const loading = ref(false);

const error = ref("");
const status = ref("");

const runningShopDomain = ref(null);
const newShopDomain = ref("");
const isBackfillEnabled = computed(() => {
  return !!currentShop.value?.cfg?.contract?.backfill;
});

/* ============================================================
   Helpers
============================================================ */
function apiHeaders(cpKey) {
  return {
    "Content-Type": "application/json",

    // API-level auth
    "x-api-key": localStorage.getItem("api_key"),

    // ComfortPages shop key
    Cpkey: cpKey ?? "",
  };
}

function getShopBaseUrl(shop) {
  const isLocal = shop.shop_domain === "localhost";
  const prot = isLocal ? "http" : "https";
  return `${prot}://${shop.shop_domain}`;
}

function resetMessages() {
  error.value = "";
  status.value = "";
}

function cleanupAfterRun() {
  runningShopDomain.value = null;
}

/* ============================================================
   Modal: Edit Sync Contract ComfortPages Side
============================================================ */
async function openEditModal(shop) {
  loading.value = true;
  error.value = "";

  try {
    const domain = getShopBaseUrl(shop);

    const res = await fetch(`${domain}/api/shop/nodezone/cfg`, {
      method: "GET",
      credentials: "include",
      headers: apiHeaders(shop.cpapikey),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || "Failed to load Node.Zone config");
    }

    shop.cfg = await res.json();
  } catch (err) {
    error.value = err.message;
    return;
  } finally {
    loading.value = false;
  }

  currentShop.value = shop;

  ModalManager.open({
    title: "ComfortPages Webshop Node.Zone Contract",
    size: "md",

    record: {
      id: shop.id,
      label: shop.shop_domain,
      cfg: shop.cfg,
    },

    component: CpNzContractEditor,
    props: {
      record: shop,
    },

    onClose(payload) {
      if (!payload?.ok || !payload.shop) return;

      const idx = shops.value.findIndex((s) => s.id == payload.shop.id);
      if (idx !== -1) {
        shops.value[idx].cfg = payload.cfg;
      }
    },
  });
}

/* ============================================================
   Load subscribed shops
============================================================ */
async function loadShops() {
  loading.value = true;
  error.value = "";

  try {
    const res = await fetch("/api/shops/nz", {
      headers: apiHeaders(),
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json.error || "Failed to load shops");
    }

    shops.value = json.shops || [];
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
}

/* ============================================================
   Add shop
============================================================ */
async function addShop() {
  const domain = newShopDomain.value.trim();
  if (!domain) return;

  error.value = "";

  try {
    const res = await fetch("/api/shops/subscribe/nz", {
      method: "POST",
      headers: apiHeaders(),
      body: JSON.stringify({
        shop_domain: domain,
      }),
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json.error || "Failed to add shop");
    }

    const shop = json.shop;

    const idx = shops.value.findIndex((s) => s.id === shop.id);
    if (idx !== -1) {
      shops.value[idx] = shop;
    } else {
      shops.value.push(shop);
    }

    shops.value.sort((a, b) => a.shop_domain.localeCompare(b.shop_domain));

    newShopDomain.value = "";
  } catch (err) {
    error.value = err.message;
  }
}

/* ============================================================
   Start manual sync (NO progress tracking)
============================================================ */
async function startSync(shop) {
  if (runningShopDomain.value) return;

  resetMessages();
  runningShopDomain.value = shop.shop_domain;

  const controller = new AbortController();

  // 90s timeout (adjust if needed)
  const timeoutMs = 90000;

  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    const domain = getShopBaseUrl(shop);

    const res = await fetch(`${domain}/api/shop/nodezone/sync/contract/all`, {
      method: "POST",
      headers: apiHeaders(shop.cpapikey),
      signal: controller.signal,
      body: JSON.stringify({
        shop_id: shop.id,
        shop_domain: shop.shop_domain,
        triggeredBy: "manual",
        customerId: localStorage.getItem("customer_id"),
      }),
    });
    const json = await res.json();

    if (!res.ok) {
      throw new Error(json.error || "Failed to start sync");
    }

    if( shop.cfg.contract.backfill ){
      status.value = `<span class="icon-checkmark"></span><h3>Backfill finished for ${shop.shop_domain} - ${json.synced} pending order${((json.synced == 0 || json.synced>1)? 's' : '')} available in poll</h3>`;

    }else{
      status.value = `<span class="icon-checkmark"></span><h3>Sync finished for ${shop.shop_domain} - ${json.synced} order${((json.synced == 0 || json.synced>1)? 's' : '')} received</h3>`;

    }
    status.value += "<br><br>";
    if( json.status && json.status.length ){
      let type = "";
      for (var i = 0; i < json.status.length; i++) {
        type = json.status[i].source_type;
        status.value += (type === 'shoporder'? 'Shoporders' : 'Shopintents') + ' remaining: '+json.status[i].cnt+'<br>';

      }

    }
    
  } catch (err) {
    if (err.name === "AbortError") {
      error.value = `Sync timed out after ${timeoutMs / 1000}s`;
    } else {
      error.value = err.message;
    }
  } finally {
    clearTimeout(timeoutId);
    cleanupAfterRun();
  }
}

/* ============================================================
   Lifecycle
============================================================ */
onMounted(loadShops);
</script>

<template>
  <section class="cp-wrap">
    <h1 class="tc">
      ComfortPages <span class="icon-arrow-right"></span>
      Node.Zone <span class="icon-cloud-sync"></span>
      Contract
    </h1>

    <div class="nz-nested">
      <div class="nz-grid-hh nz-nested-grid">
        <!-- LEFT -->
        <div class="nz-grid-item-hh nz-syn-left">
          <div class="nz-nested-1">
            <h3>Subscribed Shops</h3>

            <div v-if="loading">Loading shops…</div>

            <ul v-else class="progress shops">
              <li v-for="shop in shops" :key="shop.id">
                <span
                  class="icon-cloud"
                  :class="{ 'icon-cloud-cross': !shop.active }"
                ></span>

                <span class="li-text">{{ shop.shop_domain }}</span>

                <button
                  class="btn btn-danger btn-icon-text btn-sm"
                  @click="startSync(shop)"
                  :disabled="runningShopDomain || (!shop.cfg || !shop.cfg.active)"
                >
                  <span class="icon-cloud-sync"></span>
                  {{
                    runningShopDomain === shop.shop_domain
                      ? "Syncing…"
                      : "Start " +((shop.cfg && shop.cfg.contract.backfill)? "Backfill" : "Sync")
                  }}
                </button>

                <button class="btn btn-icon-flat" @click="openEditModal(shop)">
                  <span class="icon-cog"></span>
                </button>
              </li>
            </ul>

            <!-- ADD SHOP -->
            <div class="add-wrap">
              <input
                id="new_domain"
                type="text"
                v-model="newShopDomain"
                placeholder="new domain"
              />
              <button class="btn btn-icon" @click="addShop">
                <span class="icon-plus"></span>
              </button>
            </div>
          </div>
        </div>

        <!-- RIGHT -->
        <div class="nz-grid-item-hh">
          <div class="nz-nested-2 nz-info">
            <div v-if="!status && !error && !runningShopDomain" class="success">


              <!-- DEFAULT TEMPLATE -->
              <template v-if="!isBackfillEnabled">
                <h3>
                  <span class="icon-cloud-sync" style="font-size: 5rem;"></span>
                  Manual Sync Contract ComfortPages → Node.Zone
                </h3>

                Trigger a manual sync for all enabled contracts for a shop.
                <br /><br />
                Features at the moment include: Stock &amp; Shoporders &amp;
                Shopintents
                <br /><br />
                Both are synchronised in real-time fashion,
                <br />
                running a sync here will only check leftovers.
                <br /><br />
                Configurate your settings here, for all your shops running on
                ComfortPages.
              </template>

              <!-- BACKFILL TEMPLATE -->
              <template v-else>
                <h3 class="nz-head-warning">
                  <span class="icon-cloud-sync" style="font-size: 5rem;"></span>
                  Orders Contract Backfill Mode Enabled
                </h3>
                Orders backfill is activated.
                <br /><br />
                If you run a backfill, all orders will be removed first, and than synced with the existing orders on your ComfortPages Webshop Domain.
                <br /><br />
                The first part of this proccess entails all orders, is extremely fast, but only registers the needed orders to transfer.
                <br /><br />
                After running the backfill and disabling it again, you can run the actual transfer. This is done 250 orders per run, each run takes about 15 Seconds.
              </template>

            </div>

            <div class="nz-info-inner">
              <p v-if="status" class="success" v-html="status"></p>
              <p v-if="error" class="error">{{ error }}</p>

              <div v-if="runningShopDomain" class="tc">
                <span class="icon-cloud-sync"></span>
                <em>Syncing {{ runningShopDomain }}…</em>
              </div>
            </div>
          </div>
        </div>
        <!-- /RIGHT -->
      </div>
    </div>
  </section>
</template>


<style lang="less" scoped>

h1{
  span{
    display: inline-block;
    transform: translateY(3px);
    padding: 0px 12px;

  }
}

div.nz-grid-hh{
  max-width: 1400px;
  margin: 0 auto;

}

div.nz-syn-left{
  padding: 0px !important;

}
label {
  display: block;
  font-weight: 600;
}

div.nz-nested {
  text-align: center;
}

ul.progress {
  width: 600px;
  display: inline-block;
  margin: 0 auto;
  list-style: none;
  li {
    text-align: left;
    line-height: 2rem;
    padding: 6px;
    span:not(.article-id) {
      font-size: 1.5rem;
      color: @accent;
      display: inline-block;
      margin: 3px 10px 3px 0px;
    }
    span.li-text{
      transform: translateY(-3px);
      font-size: 15px;
      color: @text;

    }
    span.icon-cloud{
      font-size: 2rem;
      transform: translateY(4px);
      margin-left: 10px;

    }

  }
  button{
    transform: translateY(2px);
    min-width: 150px; /* such that it stays the same with the synching ... text */

  }
  button.btn-icon-flat{
    min-width: auto;
  }
  button:disabled{
    pointer-events: none !important;
    opacity: .6 !important;
  }
}

ul.shops{
  border-radius: 6px;
  border: 1px solid @gray;
  li{
    background: #F0F0F0;
    background: linear-gradient(0deg, rgba(240, 240, 240, 1) 0%, rgba(255, 255, 255, 0) 64%);

  }

}

.btn-icon-text{
  float: right;
  span{
    color: #fff !important;
  }

}

.btn-icon-flat{
  float: right;

}
.icon-cloud-cross{
  color: @gray-darker !important;
}
.nz-info{
  width: 100%;
  min-height: 60vh;
  max-height: 60vh;
  position: relative;
  overflow: hidden;

}

.nz-info h3 {
  color: @accent;

  span {
    font-size: 3rem;
    display: block;
    margin: 0 auto 12px auto;
  }
}

.nz-info-inner{
  position: absolute;
  width: 100%;
  padding: 0px 12px 20px 0px;
  top: 30%;

  span.icon-cloud-sync{
    display: block;
    margin: 0px 0px 20px 0px;
    font-size: 3.5rem;
    color: @accent;

  }

}

.success{

  color: @accent;
  margin-top: 1rem;

}

.nz-cp-i-done {
  color: @accent;
}

.nz-grid-item-hh {
  text-align: center;
  min-height: 700px;

}

.actions {
  width: 100%;
  text-align: center;

  button {
    margin: 20px;
  }
}

.error {
  color: @danger;
  margin-top: 1rem;
}

.nz-capability-header {
  display: flex;
  float: left;
  width: 116px;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
}

.nz-status-error {
  color: @danger;
  font-weight: bold;
}

.nz-status-done {
  color: @accent;
}

.add-wrap{
  text-align: right;
  padding: 20px 20px 0px 0px;
  button{
    transform: translateY(-10px);
  }
}
#new_domain{
  margin: 16px 16px 0px 0px;
  display: inline-block;

}

li.synced-articles{
  position: relative;
  padding: 0px 0px 0px 25px !important;
  &:after{
    position: absolute;
    top: 0px;
    left: -16px;
    font-family: 'icomoon' !important;
    speak: none;
    font-style: normal;
    font-weight: normal;
    font-variant: normal;
    text-transform: none;
    font-size: 1.4rem;
    color: @accent;
    display: inline;
    margin: 0px 10px 0px 15px;
    /* Better Font Rendering =========== */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    content: "\e755";

  }

}
span.article-id{
  display: inline-block;
  font-size: 1rem;
  color: @text;
  padding: 0px 4px;

}
</style>
